/**
 * Offline Status Indicator Component
 * Shows connection status and cached data information
 */

import React, { useState, useEffect } from 'react';
import { offlineManager } from '../utils/offlineManager';

interface OfflineIndicatorProps {
  showDetails?: boolean;
  className?: string;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  showDetails = false,
  className = ''
}) => {
  const [isOnline, setIsOnline] = useState(offlineManager.isConnected());
  const [showCacheInfo, setShowCacheInfo] = useState(false);
  const [cacheStats, setCacheStats] = useState(offlineManager.getCacheStats());

  useEffect(() => {
    // Subscribe to online/offline status changes
    const unsubscribe = offlineManager.addStatusListener((online) => {
      setIsOnline(online);
    });

    // Update cache stats periodically
    const updateCacheStats = () => {
      setCacheStats(offlineManager.getCacheStats());
    };

    const interval = setInterval(updateCacheStats, 5000);
    updateCacheStats(); // Initial update

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  if (isOnline && !showDetails) {
    return null; // Don't show indicator when online unless details are requested
  }

  return (
    <div className={`professional-offline-indicator ${className}`}>
      <div 
        className={`professional-offline-indicator__status ${
          isOnline ? 'professional-offline-indicator__status--online' : 'professional-offline-indicator__status--offline'
        }`}
        onClick={() => showDetails && setShowCacheInfo(!showCacheInfo)}
        style={{ cursor: showDetails ? 'pointer' : 'default' }}
      >
        <div className="professional-offline-indicator__icon">
          {isOnline ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <path d="M2 2l20 20"/>
            </svg>
          )}
        </div>
        
        <span className="professional-offline-indicator__text">
          {isOnline ? 'Online' : 'Offline'}
        </span>
        
        {!isOnline && cacheStats.size > 0 && (
          <span className="professional-offline-indicator__cache-count">
            ({cacheStats.size} cached)
          </span>
        )}
      </div>
      
      {showDetails && showCacheInfo && (
        <div className="professional-offline-indicator__details">
          <div className="professional-offline-indicator__cache-info">
            <h4>Cached Data</h4>
            <div className="professional-offline-indicator__cache-stats">
              <div>Entries: {cacheStats.size}</div>
              <div>Size: {Math.round(cacheStats.totalSize / 1024)}KB</div>
            </div>
            
            {cacheStats.entries.length > 0 && (
              <div className="professional-offline-indicator__cache-entries">
                {cacheStats.entries.slice(0, 5).map((entry, index) => (
                  <div key={index} className="professional-offline-indicator__cache-entry">
                    <div className="professional-offline-indicator__cache-key">
                      {entry.key.split('/').pop() || entry.key}
                    </div>
                    <div className="professional-offline-indicator__cache-meta">
                      {entry.expiresIn > 0 
                        ? `Expires in ${Math.round(entry.expiresIn / 1000)}s`
                        : 'Expired'
                      }
                    </div>
                  </div>
                ))}
                
                {cacheStats.entries.length > 5 && (
                  <div className="professional-offline-indicator__cache-more">
                    +{cacheStats.entries.length - 5} more entries
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Connection Status Hook
 */
export const useConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(offlineManager.isConnected());
  const [cacheStats, setCacheStats] = useState(offlineManager.getCacheStats());

  useEffect(() => {
    const unsubscribe = offlineManager.addStatusListener(setIsOnline);
    
    const updateCacheStats = () => {
      setCacheStats(offlineManager.getCacheStats());
    };
    
    const interval = setInterval(updateCacheStats, 5000);
    updateCacheStats();

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  return {
    isOnline,
    cacheStats,
    hasCachedData: cacheStats.size > 0
  };
};

/**
 * Offline Banner Component
 */
export const OfflineBanner: React.FC = () => {
  const { isOnline, hasCachedData } = useConnectionStatus();

  if (isOnline) {
    return null;
  }

  return (
    <div className="professional-offline-banner">
      <div className="professional-offline-banner__content">
        <div className="professional-offline-banner__icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <path d="M2 2l20 20"/>
          </svg>
        </div>
        
        <div className="professional-offline-banner__message">
          <strong>You're currently offline.</strong>
          {hasCachedData 
            ? ' Some features are available using cached data.'
            : ' Limited functionality available until connection is restored.'
          }
        </div>
      </div>
    </div>
  );
};